import { React, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import api from '../components/setupAxios';
import logo from '../img/centennialcollege_logo_horizontal_rgb.jpg';
import {jwtDecode} from 'jwt-decode';
import { AppBar, Toolbar, IconButton, Typography, Button, Box, Menu, MenuItem } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu'; 

function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [userRole, setUserRole] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      try {
        const decoded = jwtDecode(storedToken);
        setUserRole(decoded.role);
        setToken(storedToken); 
      } catch (error) {
        console.error('Error decoding token:', error);
        localStorage.removeItem('token');
        setToken(null);
      }
    }
  }, []); 

  useEffect(() => {
    const handleStorageChange = () => {
      const updatedToken = localStorage.getItem('token');
      setToken(updatedToken);
      if (updatedToken) {
        try {
          const decoded = jwtDecode(updatedToken);
          setUserRole(decoded.role);
        } catch (error) {
          console.error('Error decoding token:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      } else {
        setUserRole(null);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleLogout = async () => {
    try {
      await api.get('/auth/logout', {
        withCredentials: true
      });
      localStorage.removeItem('token');
      setToken(null);
      setUserRole(null);
      window.dispatchEvent(new Event("storage"));

      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".nav-links") && !event.target.closest(".menu-toggle")) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const homePath = token
    ? userRole === 'student'
      ? '/'
      : userRole === 'assistant'
        ? '/assistant-dashboard'
        : userRole === 'staff'
          ?'admin-dashboard'
          :'/'
    : '/';

    const handleMenuOpen = (event) => {
      setMenuAnchor(event.currentTarget);
    };
    const handleMenuClose = () => {
      setMenuAnchor(null);
    };
  
    const isActive = (path) => (location.pathname === path ? 'underline' : 'none');

    return (
      <AppBar position="sticky">
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <Box
              component="img"
              src={logo}
              alt="Logo"
              sx={{ height: 40, mr: 2, display: { xs: 'none', sm: 'block' }}}
            />
            <Typography variant="h6" noWrap component="div">
              Open-Lab Management
            </Typography>
          </Box>
  
          {/* Use flexRow to occupy the remaining space and push the right button to the right */}
          <Box sx={{ flexGrow: 1 }} />
  
          {/* Large screen: directly display buttons; Small screen: hidden */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2 }}>
            <Button
              color="inherit"
              component={Link}
              to={homePath}
              sx={{ textDecoration: isActive(homePath) }}
            >
              HOME
            </Button>
  
            {(userRole === 'assistant' || userRole === 'staff') && (
              <Button
                color="inherit"
                component={Link}
                to="/manage-students"
                sx={{ textDecoration: isActive('/manage-students') }}
              >
                MANAGE STUDENTS
              </Button>
            )}

            { (userRole === 'staff') && (
                <Button
                  color="inherit"
                  component={Link}
                  to="/assistant-dashboard"
                  sx={{ textDecoration: isActive('/assistant-dashboard') }}
                >
                  VIEW SESSION
                </Button>
              )}
              
            {token ? (
              <Button color="inherit" onClick={handleLogout}>
                LOGOUT
              </Button>
            ) : (
              <Button
                color="inherit"
                component={Link}
                to="/login"
                sx={{ textDecoration: isActive('/login') }}
              >
                LOGIN
              </Button>
            )}
          </Box>
  
          {/* Small screen: display hamburger menu icon; Large screen: hidden */}
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              color="inherit"
              onClick={handleMenuOpen}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
            >
              {/* This is the dropdown menu item for the small screen */}
              <MenuItem
                component={Link}
                to={homePath}
                onClick={handleMenuClose}
                sx={{ textDecoration: isActive(homePath) }}
              >
                HOME
              </MenuItem>
  
              {(userRole === 'assistant' || userRole === 'staff') && (
                <MenuItem
                  component={Link}
                  to="/manage-students"
                  onClick={handleMenuClose}
                  sx={{ textDecoration: isActive('/manage-students') }}
                >
                  MANAGE STUDENTS
                </MenuItem>
              )}

              { (userRole === 'staff') && (
                <MenuItem
                  color="inherit"
                  component={Link}
                  to="/assistant-dashboard"
                  sx={{ textDecoration: isActive('/assistant-dashboard') }}
                >
                  VIEW SESSION
                </MenuItem>
              )}
  
              {token ? (
                <MenuItem
                  onClick={() => {
                    handleLogout();
                    handleMenuClose();
                  }}
                >
                  LOGOUT
                </MenuItem>
              ) : (
                <MenuItem
                  component={Link}
                  to="/login"
                  onClick={handleMenuClose}
                  sx={{ textDecoration: isActive('/login') }}
                >
                  LOGIN
                </MenuItem>
              )}
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
    );
  }
  

export default Navbar;
